import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar as CalendarIcon, Filter, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { complianceData, categories, categoryColors } from '../data/complianceData';

const ComplianceCalendar = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedMonth, setSelectedMonth] = useState('All');

  const months = ['All', 'January', 'February', 'March', 'April', 'May', 'June', 
                  'July', 'August', 'September', 'October', 'November', 'December'];

  const filteredCompliances = useMemo(() => {
    return complianceData.filter(item => {
      const categoryMatch = selectedCategory === 'All' || item.category === selectedCategory;
      const monthMatch = selectedMonth === 'All' || 
        new Date(item.date).toLocaleString('en-US', { month: 'long' }) === selectedMonth;
      return categoryMatch && monthMatch;
    }).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [selectedCategory, selectedMonth]);

  const getUpcomingCompliances = () => {
    const today = new Date();
    return filteredCompliances.filter(item => new Date(item.date) >= today).slice(0, 5);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getDaysUntil = (dateString) => {
    const today = new Date();
    const targetDate = new Date(dateString);
    const diffTime = targetDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusBadge = (dateString) => {
    const daysUntil = getDaysUntil(dateString);
    if (daysUntil < 0) return { text: 'Overdue', class: 'bg-red-100 text-red-800' };
    if (daysUntil <= 7) return { text: `${daysUntil} days left`, class: 'bg-orange-100 text-orange-800' };
    if (daysUntil <= 30) return { text: `${daysUntil} days left`, class: 'bg-yellow-100 text-yellow-800' };
    return { text: `${daysUntil} days left`, class: 'bg-green-100 text-green-800' };
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </Button>
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-6 h-6 text-teal-600" />
              <h1 className="text-xl font-bold text-gray-900">Compliance Calendar</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Compliance Calendar 2026</h2>
            <p className="text-lg text-gray-600">
              Stay on top of all your compliance deadlines
            </p>
          </div>

          {/* Upcoming Deadlines Highlight */}
          <Card className="mb-8 border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-white">
            <CardHeader>
              <CardTitle className="text-teal-900">Upcoming Deadlines</CardTitle>
              <CardDescription>Next 5 compliance dates to watch</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {getUpcomingCompliances().map(item => {
                  const status = getStatusBadge(item.date);
                  return (
                    <div key={item.id} className="flex items-center justify-between p-4 bg-white rounded-lg border">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{item.title}</h4>
                        <p className="text-sm text-gray-600">{item.description}</p>
                      </div>
                      <div className="text-right ml-4">
                        <p className="font-semibold text-gray-900">{formatDate(item.date)}</p>
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${status.class} mt-1`}>
                          {status.text}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Filters */}
          <div className="bg-white p-6 rounded-xl shadow-md mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-5 h-5 text-teal-600" />
              <h3 className="font-semibold text-gray-900">Filters</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {/* Category Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <div className="flex flex-wrap gap-2">
                  {categories.map(category => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedCategory === category
                          ? 'bg-teal-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>

              {/* Month Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Month</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                >
                  {months.map(month => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Compliance List */}
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">
                All Compliances ({filteredCompliances.length})
              </h3>
              <Button variant="outline" className="flex items-center gap-2">
                <Download className="w-4 h-4" />
                Download PDF
              </Button>
            </div>

            <div className="grid gap-4">
              {filteredCompliances.map(item => {
                const status = getStatusBadge(item.date);
                return (
                  <Card key={item.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${categoryColors[item.category]}`}>
                              {item.category}
                            </span>
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                              {item.frequency}
                            </span>
                          </div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-1">{item.title}</h4>
                          <p className="text-sm text-gray-600">{item.description}</p>
                        </div>
                        <div className="text-right ml-6">
                          <p className="text-2xl font-bold text-gray-900 mb-1">{formatDate(item.date)}</p>
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${status.class}`}>
                            {status.text}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 text-center p-8 bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl border-2 border-teal-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Never Miss a Compliance Deadline
            </h3>
            <p className="text-gray-700 mb-6">
              Let us handle all your compliance requirements while you focus on growing your business
            </p>
            <Button
              size="lg"
              onClick={() => {
                navigate('/');
                setTimeout(() => {
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
              className="bg-teal-600 hover:bg-teal-700"
            >
              Get Compliance Support
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceCalendar;
